import { ExchangeMark } from "@/components/ui/kit";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";
import { marketProviderLabel, sortMarketProviders, type MarketListing, type MarketTradeType } from "@/lib/market-provider";

const ITEM_HEIGHT_PX = 40;

/** BTCUSDT, BTC_USDT, BTC-USDT → BTC/USDT for the toolbar chip. Unknown quotes stay as stored. */
function displayPair(symbol: string): string {
  const quotes = ["USDT", "USDC", "BUSD", "USD", "EUR"];
  const split = symbol.match(/^(.+)[_-](.+)$/);
  if (split && quotes.includes(split[2]!)) return `${split[1]}/${split[2]}`;
  for (const quote of quotes) {
    if (symbol.endsWith(quote) && symbol.length > quote.length) {
      return `${symbol.slice(0, -quote.length)}/${quote}`;
    }
  }
  return symbol;
}

export type SymbolMarketSelectProps = {
  symbol: string;
  provider: string;
  /** Market of the selected symbol, from its market config (never the symbol name). */
  market?: MarketTradeType;
  /** Enabled marketplace provider ids (e.g. binance, mexc). */
  providers: string[];
  /** Load every symbol a provider lists, on each of its markets. */
  loadSymbols: (provider: string) => Promise<MarketListing[]>;
  onChange: (next: { symbol: string; provider: string; market: MarketTradeType }) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  title?: string;
  /** Stretch to fill the form row (Settings). Default sizes to the symbol name (toolbar). */
  fullWidth?: boolean;
  /**
   * `chip` — toolbar chip: exchange mark, symbol, muted detail.
   * `big` — full-width form field with the same content, larger (New backtest).
   * The dialog picker is the same for all.
   */
  variant?: "field" | "chip" | "big";
  /** Second line next to the symbol for chip/big, e.g. "Binance Futures". */
  detail?: string;
  /** `big` only: pulse until the user picks, for a default they should choose themselves. */
  attention?: boolean;
};

type Row = MarketListing & { provider: string };
type Kind = "all" | "spot" | "futures";

const MAX_ROWS = 300;
const MAIN_QUOTES = ["USDT", "USDC", "USD"];
/** ETH/USDT, ETH_USDT (MEXC futures) and ETH-USD (Revolut X) all search as ETHUSDT. */
const plain = (s: string) => s.toUpperCase().replace(/[/_-]/g, "");

/**
 * TradingView-style symbol search: one list across exchanges, each row shows
 * the symbol with its exchange on the right. Filter by exchange and by
 * Spot/Futures; picking a row commits symbol + exchange together.
 */
export function SymbolMarketSelect({
  symbol,
  provider,
  market,
  providers,
  loadSymbols,
  onChange,
  placeholder = "Symbol…",
  searchPlaceholder = "Search symbols…",
  emptyMessage = "No matches",
  disabled = false,
  className = "",
  title = "Select symbol",
  fullWidth = false,
  variant = "field",
  detail,
  attention = false,
}: SymbolMarketSelectProps) {
  const listId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const loadGen = useRef(0);

  const [open, setOpen] = useState(false);
  /** "" = all exchanges */
  const [exchange, setExchange] = useState("");
  const [kind, setKind] = useState<Kind>("all");
  const [byProvider, setByProvider] = useState<Record<string, MarketListing[]>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(-1);

  const sortedProviders = useMemo(() => sortMarketProviders(providers), [providers]);
  const label = symbol || placeholder;

  const rows = useMemo(() => {
    const needle = plain(query.trim());
    const out: Row[] = [];
    for (const p of sortedProviders) {
      if (exchange && p !== exchange) continue;
      for (const listing of byProvider[p] || []) {
        if (kind !== "all" && listing.market !== kind) continue;
        if (needle && !plain(listing.symbol).includes(needle)) continue;
        out.push({ ...listing, provider: p });
      }
    }
    // Best match first: the query as the base coin against a main quote
    // (ETH → ETHUSDT), then symbols starting with it, then the rest A–Z.
    const rank = (sym: string) => {
      if (!needle) return 3;
      const bare = plain(sym);
      const main = MAIN_QUOTES.findIndex((q) => bare === needle + q);
      if (main >= 0) return main / 10;
      if (bare === needle) return 0;
      return bare.startsWith(needle) ? 1 : 2;
    };
    out.sort((a, b) => {
      const byRank = rank(a.symbol) - rank(b.symbol);
      if (byRank) return byRank;
      const bySymbol = a.symbol.localeCompare(b.symbol, undefined, { sensitivity: "base" });
      return bySymbol || sortedProviders.indexOf(a.provider) - sortedProviders.indexOf(b.provider);
    });
    return out;
  }, [byProvider, sortedProviders, exchange, kind, query]);
  const shown = rows.slice(0, MAX_ROWS);

  const fetchAll = useCallback(async () => {
    const gen = ++loadGen.current;
    setLoading(true);
    setLoadError(null);
    const results = await Promise.all(
      sortedProviders.map(async (p) => {
        try {
          const list = await loadSymbols(p);
          return [p, Array.isArray(list) ? list : []] as const;
        } catch (e: unknown) {
          return [p, e instanceof Error ? e : new Error("Failed to load symbols")] as const;
        }
      })
    );
    if (gen !== loadGen.current) return;
    const next: Record<string, MarketListing[]> = {};
    let failed: string | null = null;
    for (const [p, res] of results) {
      if (res instanceof Error) failed = failed || res.message;
      else next[p] = res as MarketListing[];
    }
    setByProvider(next);
    setLoadError(Object.keys(next).length ? null : failed);
    setLoading(false);
  }, [loadSymbols, sortedProviders]);

  function close() {
    setOpen(false);
    setHighlight(-1);
    setQuery("");
    setLoadError(null);
  }

  useEffect(() => {
    if (!open) return;
    // Start on what's selected: its market type, across all exchanges.
    setExchange("");
    setKind(symbol && market ? market : "all");
    setQuery("");
    setHighlight(0);
    void fetchAll();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open only
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (highlight < 0 || !listRef.current) return;
    const item = listRef.current.children[highlight] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  function selectRow(row: Row) {
    onChange({ symbol: row.symbol, provider: row.provider, market: row.market });
    close();
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => (shown.length ? (i + 1) % shown.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => (shown.length ? (i <= 0 ? shown.length - 1 : i - 1) : -1));
    } else if (e.key === "Enter" && highlight >= 0 && shown[highlight]) {
      e.preventDefault();
      selectRow(shown[highlight]);
    }
  }

  const emptyText = loading ? "Loading symbols…" : loadError ? loadError : emptyMessage;
  const filterChip = (on: boolean) =>
    `inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
      on
        ? "border-[var(--primary)] bg-[var(--primary-soft)] font-medium text-[var(--text-primary)]"
        : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
    }`;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={symbol ? `${symbol} · ${detail || marketProviderLabel(provider)}` : placeholder}
        className={
          variant === "big"
            ? `flex w-full cursor-pointer items-center gap-3 rounded-[12px] border border-[var(--border)] bg-[var(--bg-card-elevated)] px-3.5 py-3 text-left hover:border-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-50 ${attention ? "field-attention" : ""} ${className}`
            : variant === "chip"
              ? `inline-flex h-auto w-max max-w-none shrink-0 cursor-pointer items-center gap-2 self-center whitespace-nowrap rounded-[9px] border border-[var(--border)] bg-[var(--bg-secondary)] py-0.5 pl-0.5 pr-2.5 text-left text-xs leading-none text-[var(--text-primary)] hover:border-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-50 ${className}`
              : fullWidth
                ? `flex h-10 min-h-10 w-full cursor-pointer items-center gap-1.5 rounded-[var(--radius-input)] border border-[var(--input-border)] bg-[var(--input-bg)] px-2.5 text-left text-sm text-[var(--text-primary)] hover:border-[var(--text-muted)] disabled:cursor-not-allowed disabled:opacity-50 ${className}`
                : `inline-flex h-10 min-h-10 w-max min-w-max max-w-none shrink-0 cursor-pointer items-center gap-1.5 self-start whitespace-nowrap rounded-[var(--radius-input)] border border-[var(--input-border)] bg-[var(--input-bg)] px-2.5 text-left text-sm text-[var(--text-primary)] hover:border-[var(--text-muted)] disabled:cursor-not-allowed disabled:opacity-50 ${className}`
        }
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
      >
        {variant === "big" ? (
          <>
            {provider ? <ExchangeMark provider={provider} size={32} /> : null}
            <span className="min-w-0 flex-1">
              <b className={`block truncate text-[15px] font-semibold ${symbol ? "text-[var(--text-primary)]" : "font-normal text-[var(--placeholder)]"}`}>
                {symbol ? displayPair(symbol) : placeholder}
              </b>
              <span className="block truncate text-xs text-[var(--text-muted)]">{detail || marketProviderLabel(provider)}</span>
            </span>
            {attention ? (
              <span className="flex shrink-0 items-center gap-1.5 rounded-[8px] bg-[var(--primary)] px-2.5 py-1.5 text-xs font-medium text-[var(--on-primary)]">
                <Search size={14} />
                Choose symbol
              </span>
            ) : (
              <span className="flex shrink-0 items-center gap-1.5 text-xs text-[var(--text-muted)]">
                <Search size={14} />
                Change
              </span>
            )}
          </>
        ) : (
          <>
            {provider ? (
              variant === "chip" ? (
                <span className="inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-[6px] [&_img]:h-3.5 [&_img]:w-auto [&_img]:max-w-5 [&>span>span]:!size-5">
                  <ExchangeMark provider={provider} size={20} />
                </span>
              ) : (
                <ExchangeMark provider={provider} size={20} />
              )
            ) : null}
            {variant === "chip" ? (
              <>
                <span className={`font-semibold ${symbol ? "" : "font-normal text-[var(--placeholder)]"}`}>{displayPair(label)}</span>
                {detail ? <span className="font-normal text-[var(--text-muted)]">{detail}</span> : null}
              </>
            ) : (
              <>
                <span className={`min-w-0 ${fullWidth ? "flex-1 truncate" : ""} ${symbol ? "" : "text-[var(--placeholder)]"}`}>{label}</span>
                <ChevronDown size={14} className="shrink-0 text-[var(--text-muted)]" />
              </>
            )}
          </>
        )}
      </button>

      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <div className="flex h-[min(80vh,620px)] w-full max-w-[560px] flex-col overflow-hidden rounded-[16px] border border-[var(--border)] bg-[var(--bg-card)] shadow-lg">
              <div className="flex shrink-0 items-center gap-2.5 border-b border-[var(--divider)] px-4 py-3">
                <Search size={16} className="shrink-0 text-[var(--text-muted)]" />
                <input
                  ref={searchRef}
                  type="text"
                  className="!h-auto !min-h-0 min-w-0 flex-1 !border-0 !bg-transparent !p-0 !text-[15px] text-[var(--text-primary)] !shadow-none !outline-none placeholder:text-[var(--placeholder)]"
                  placeholder={searchPlaceholder}
                  value={query}
                  aria-label={title}
                  aria-autocomplete="list"
                  aria-controls={listId}
                  aria-activedescendant={highlight >= 0 ? `${listId}-option-${highlight}` : undefined}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlight(0);
                  }}
                  onKeyDown={onSearchKeyDown}
                />
                <button
                  type="button"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
                  aria-label="Close"
                  onClick={close}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-[var(--divider)] px-3 py-2">
                <div className="flex rounded-[9px] border border-[var(--border)] bg-[var(--bg-secondary)] p-0.5" role="group" aria-label="Market type">
                  {(["all", "spot", "futures"] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={kind === k}
                      onClick={() => {
                        setKind(k);
                        setHighlight(0);
                      }}
                      className={`rounded-[7px] px-2.5 py-0.5 text-xs ${
                        kind === k ? "bg-[var(--primary-soft)] font-medium text-[var(--primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      }`}
                    >
                      {k === "all" ? "All" : k === "spot" ? "Spot" : "Futures"}
                    </button>
                  ))}
                </div>
                <span className="mx-0.5 h-4 w-px bg-[var(--divider)]" aria-hidden />
                <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto" role="group" aria-label="Exchange">
                  <button
                    type="button"
                    aria-pressed={exchange === ""}
                    className={filterChip(exchange === "")}
                    onClick={() => {
                      setExchange("");
                      setHighlight(0);
                    }}
                  >
                    All exchanges
                  </button>
                  {sortedProviders.map((p) => (
                    <button
                      key={p}
                      type="button"
                      aria-pressed={exchange === p}
                      className={filterChip(exchange === p)}
                      onClick={() => {
                        setExchange(p);
                        setHighlight(0);
                      }}
                    >
                      {marketProviderLabel(p)}
                    </button>
                  ))}
                </div>
              </div>

              <ul ref={listRef} id={listId} role="listbox" className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0 py-1">
                {shown.length === 0 ? (
                  <li className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">{emptyText}</li>
                ) : (
                  shown.map((row, index) => {
                    const active = index === highlight;
                    const selected = row.symbol === symbol && row.provider === provider && (!market || row.market === market);
                    return (
                      <li
                        key={`${row.provider}:${row.market}:${row.symbol}`}
                        id={`${listId}-option-${index}`}
                        role="option"
                        aria-selected={selected}
                        className={
                          "flex cursor-pointer items-center gap-3 px-4 text-[13px] " +
                          (active ? "bg-[var(--bg-hover)]" : "")
                        }
                        style={{ minHeight: ITEM_HEIGHT_PX }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectRow(row);
                        }}
                        onMouseEnter={() => setHighlight(index)}
                      >
                        <span className={`num min-w-0 flex-1 truncate ${selected ? "font-semibold text-[var(--primary)]" : "font-medium text-[var(--text-primary)]"}`}>
                          {row.symbol}
                        </span>
                        <span className="shrink-0 text-xs text-[var(--text-muted)]">{row.market === "futures" ? "Futures" : "Spot"}</span>
                        <span className="flex w-[118px] shrink-0 items-center justify-end gap-2 text-xs text-[var(--text-secondary)]">
                          {marketProviderLabel(row.provider)}
                          <ExchangeMark provider={row.provider} size={20} />
                        </span>
                      </li>
                    );
                  })
                )}
              </ul>
              {rows.length > MAX_ROWS && (
                <div className="shrink-0 border-t border-[var(--divider)] px-4 py-2 text-xs text-[var(--text-muted)]">
                  Showing the first {MAX_ROWS} of {rows.length.toLocaleString()}. Type to narrow it down.
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
